import { KeyboardArrowDownSharp, KeyboardArrowUpSharp } from '@mui/icons-material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { Autocomplete, TextField, Checkbox, InputAdornment } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldErrorBox, FieldErrorText } from 'components/Fields/BaseTextFieldController/styled';
import { ChipW, ChipLabel, ChipDeleteBtn } from 'components/Fields/CustomSelectedValue/styled';
import { theme } from 'styles';

import { CheckboxWrapper, GroupWrapper, AccordinWrapper } from './styles';
interface SelectionType {
  code: string;
  label: string;
  group: string;
  isManager?: boolean;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface UserSelectProps {
  allUserCategoriesList: SelectionType[];
  selectedPlaces: SelectionType[];
  setSelectedPlaces: Dispatch<SetStateAction<SelectionType[]>>;
  setFirstChange: Dispatch<SetStateAction<boolean>>;
  error?: string;
}

export default function PlaceSelect({
  allUserCategoriesList,
  selectedPlaces,
  setSelectedPlaces,
  setFirstChange,
  error,
}: UserSelectProps) {
  const { t } = useTranslation();
  const checkOption = (option: SelectionType) => {
    const check = selectedPlaces.some((c) => c.code === option.code);
    return check;
  };

  const changeData = (data: SelectionType) => {
    setSelectedPlaces((prevState) => {
      const isSelected = prevState.some((c) => c.code === data.code);
      if (isSelected) {
        // If selected, remove it from the array
        return prevState.filter((c) => c.code !== data.code);
      } else {
        // If not selected, add it to the array
        return [...prevState, data];
      }
    });
  };
  const checkGroup = (group: string) => {
    const groupLength = allUserCategoriesList.filter((c) => c.group === group).length;
    const selectedGroupLength = selectedPlaces.filter((c) => c.group === group).length;
    return groupLength === selectedGroupLength;
  };

  const selectGroup = (group: string) => {
    const groupedCategories = allUserCategoriesList.filter((c) => c.group === group);
    const selectedGroupCategories = selectedPlaces.filter((c) => c.group === group);

    if (selectedGroupCategories.length > 0) {
      setSelectedPlaces((prevState) => [...prevState.filter((c) => c.group !== group)]);
    } else {
      setSelectedPlaces((prevState) => [...prevState, ...groupedCategories]);
    }
  };

  const unselectOption = (code: string) => {
    setSelectedPlaces((prevState) => prevState.filter((c) => c.code !== code));
  };

  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const checkFocus = () => {
    if (inputRef.current) {
      setIsFocused(document.activeElement === inputRef.current);
    }
  };

  return (
    <>
      <Autocomplete
        id="places-select"
        open={isFocused}
        onOpen={() => setIsFocused(true)}
        onClose={() => setIsFocused(false)}
        options={allUserCategoriesList as SelectionType[]}
        onChange={(_, option) => setSelectedPlaces([...(option as SelectionType[])])}
        value={selectedPlaces}
        inputValue=""
        autoHighlight
        multiple
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        groupBy={(option) => option.group}
        popupIcon={null}
        openOnFocus
        renderOption={(option, value) => (
          <CheckboxWrapper>
            <Checkbox
              sx={{
                color: theme.palette.info.main,
                marginLeft: '30px',
                '&.Mui-checked': {
                  color: theme.palette.info.main,
                },
              }}
              key={option.id}
              icon={icon}
              checkedIcon={checkedIcon}
              checked={checkOption(value)}
              onChange={() => {
                changeData(value);
                setFirstChange(true);
              }}
            />
            {value.label} {value.isManager && '*'}
          </CheckboxWrapper>
        )}
        renderGroup={(params) => (
          <>
            {params.group === t('post.create.form.placeGroups.newsfeed') ? (
              <GroupWrapper>
                <Checkbox
                  key={params.group}
                  sx={{
                    color: theme.palette.info.main,
                    '&.Mui-checked': {
                      color: theme.palette.info.main,
                    },
                  }}
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={checkGroup(params.group)}
                  onChange={() => selectGroup(params.group)}
                />
                <span>{params.group}</span>
              </GroupWrapper>
            ) : (
              <Accordion
                sx={{ boxShadow: 'none', '&:before': { display: 'none' }, margin: '0 !important' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  disabled={params.group === t('post.create.form.placeGroups.newsfeed')}
                  sx={{ minHeight: '40px', '& .MuiAccordionSummary-content': { margin: '4px 0' } }}
                >
                  <AccordinWrapper>
                    <span>{params.group}</span>
                  </AccordinWrapper>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '0 0 4px 0' }}>{params.children}</AccordionDetails>
              </Accordion>
            )}
          </>
        )}
        renderInput={(params) => {
          return (
            <>
              <TextField
                inputRef={inputRef}
                {...params}
                placeholder={
                  !selectedPlaces?.length ? t('post.create.form.placeholders.place') : ''
                }
                variant="standard"
                onFocus={checkFocus}
                onBlur={checkFocus}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        margin: 0,
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        const inputElement = (
                          params.InputProps.ref as React.RefObject<HTMLInputElement>
                        ).current;
                        if (isFocused) {
                          inputElement?.focus();
                          setIsFocused(false);
                        } else {
                          inputElement?.blur();
                          setIsFocused(true);
                        }
                      }}
                    >
                      {isFocused ? (
                        <KeyboardArrowUpSharp sx={{ color: '#b2b2b2', fontSize: 24 }} />
                      ) : (
                        <KeyboardArrowDownSharp sx={{ color: '#b2b2b2', fontSize: 24 }} />
                      )}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (e) => {
                    if (e.key === 'Backspace' || e.key === 'Delete') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  },
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 'auto',
                    minHeight: '56px',
                    position: 'relative',
                    flexWrap: 'wrap',
                    gap: '8px',
                    alignItems: 'center',
                    border: '1px solid #DDDDDD',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    padding: '6px 48px 6px 8px',
                    marginBottom: '8px',
                  },
                  '& .MuiInput-root:before': { border: 'none' },
                  '& .MuiInput-root:after': { border: 'none' },
                  '& .MuiInput-root:hover:not(.Mui-disabled):before': { border: 'none' },
                  '& .MuiInputBase-input': {
                    padding: '0',
                    border: 'none',
                  },
                }}
              />
            </>
          );
        }}
        renderTags={() => {
          return selectedPlaces.map((tag) => {
            const hasCategoryPermission = allUserCategoriesList.find(
              (category) => category.code === tag.code,
            );

            return (
              <ChipW key={tag.code}>
                <ChipLabel>{tag.label}</ChipLabel>
                {hasCategoryPermission && (
                  <ChipDeleteBtn type="button" onClick={() => unselectOption(tag.code)}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </ChipDeleteBtn>
                )}
              </ChipW>
            );
          });
        }}
      />
      {error && (
        <FieldErrorBox>
          <InfoIcon />
          <FieldErrorText>{error}</FieldErrorText>
        </FieldErrorBox>
      )}
    </>
  );
}
